import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { AntiCheating } from '../utils';
import supabase from '../utils/SupabaseConfig';
import { ReportQuestionPopup, CountdownTimer, StickyMenu, FAQ, ConfirmationScreen, AbandonedScreen, CompletedScreen, AbandonConfirmationModal, ConfirmationModal, CompletionProgressBar, StatusPill, TimeExpiredBox, Footer } from '../ui';
import { useSession } from '../../context/SessionContext';
import { useNavigation } from '../../hooks/useNavigation';
import { EVALUATION_ENDPOINTS, OTHER_ENDPOINTS } from '../../constants/apiEndpoints';
import { CodeSnippetViewer } from '../questions';
import { getLanguageFromSkills } from '../utils/languageDetection';
import { useCheatingDetection, useTimer, useReportPopup, useAbandonConfirmation } from '../../hooks';
import { CompletionCircle, ElapsedTime } from './components/ChallengeInterview';
import {
  MultiOptionsQuestion,
  SingleOptionQuestion,
  TrueFalseQuestion,
  ShortTextQuestion,
  LongTextQuestion,
  AudioAnswerQuestionWithSave,
  VideoAnswerQuestionWithSave,
  CodeCompletionQuestion,
  CodeRefactoringQuestion,
  CodeExplanationQuestion,
  FindTheBugQuestion,
  SecurityTaskQuestion,
  PerformanceOptimizationQuestion,
  CodeWriteQuestion
} from '../questions';
import { getQuestionTypeLabel } from '../questions/utils/questionHelpers';

function Challenge() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { sessionData, updateSessionData } = useSession();
  const { navigatePrevious } = useNavigation();
  
  // Get eid from URL path parameter
  const eid = params.eid;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const { cheatingEvents, handleCheatingDetected, cleanup } = useCheatingDetection();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [challengeInstanceId, setChallengeInstanceId] = useState(null); // Store the challenge instance ID from the server
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false); // State for submit confirmation modal
  const { reportPopupOpen, currentQuestionId, currentQuestion, openReportPopup, closeReportPopup } = useReportPopup();

  // Helper function to format responses for submission - memoized with useCallback
  const formatResponses = useCallback(() => {
    if (!data || !data.questions) return [];
    
    // Create an array of question answers in the structured format
    return data.questions.map(question => {
      const userResponse = responses[question.id];
      
      // For single-answer and multiple-answer questions, include otherAnswer field
      if (question.type === 'single-answer' || question.type === 'multiple-answer') {
        return {
          id: question.id,
          answer: userResponse?.answer || (question.type === 'multiple-answer' ? [] : ''),
          otherAnswer: userResponse?.otherAnswer || ''
        };
      }
      // For audio-answer questions, flatten the structure
      else if (question.type === 'audio-answer' && userResponse && typeof userResponse === 'object') {
        return {
          id: question.id,
          answer: userResponse.answer || '',
          file_id: userResponse.file_id,
          file_key: userResponse.file_key
        };
      }
      // For video-answer questions, flatten the structure
      else if (question.type === 'video-answer' && userResponse && typeof userResponse === 'object') {
        return {
          id: question.id,
          answer: userResponse.answer || '',
          file_id: userResponse.file_id,
          file_key: userResponse.file_key
        };
      }
      // For other question types, keep the original format
      else {
        return {
          id: question.id,
          answer: userResponse || ''
        };
      }
    });
  }, [data, responses]);

  // Submit challenge function
  const submitChallenge = async () => {
    setIsSubmitting(true);
    setShowSubmitConfirm(false); // Close the confirmation modal

    if (data && data.questions) {
      // Get formatted question answers using the shared function
      const questionAnswers = formatResponses();
      
      try {
        // Create the data structure to send to the server
        const dataToSend = {
          ci_id: challengeInstanceId,
          questionAnswers: questionAnswers, // Array of question answers in the new format
          cheatingEvents: [...cheatingEvents] // Cheating events array at root level
        };
        
        // Log the data being submitted to the server
        console.log('Submitting Challenge Data:', JSON.stringify(dataToSend, null, 2));
        
        // Send the data to the webhook
        const response = await fetch(EVALUATION_ENDPOINTS.SAVE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend),
        });

        if (!response.ok) {
          throw new Error(`Failed to save data: ${response.status} ${response.statusText}`);
        }
        
        // Parse the response to check the status
        const responseData = await response.json();

        console.log("Challenge responseData: ",responseData);
        
        // Check if the response contains the expected status using optional chaining
        if (responseData?.[0]?.challenge_status !== 'cs_submitted') {
          throw new Error('Server did not confirm challenge submission. Expected status: cs_submitted');
        }
        
        // Log successful submission
        console.log('Challenge submitted successfully!', new Date().toISOString());
        
        // Update session data to mark challenge as completed with the status from the server
        setChallengeStatus(responseData[0].challenge_status);
        updateSessionData({ challenge_status: responseData[0].challenge_status });
        
        // Reset submission state before navigation
        setIsSubmitting(false);
        
        // Navigate to the next page (Finished)
        navigate(`/finished/${eid}`);
      } catch (err) {
        console.error('Error saving challenge data:', err);
        // Log detailed error information for debugging
        console.log('Submission Error Details:', {
          timestamp: new Date().toISOString(),
          errorMessage: err.message,
          responseData: responses,
          eid: eid
        });
        
        setError(err.message);
        setIsSubmitting(false);
        
        // Show user-friendly error message
        alert("There was an error submitting your challenge. Your responses are still saved. Please try submitting again.");
      }
    }
  };

  // State for countdown timer
  const [startDate, setStartDate] = useState(null);
  const [timerDuration, setTimerDuration] = useState(null);
  const { timerExpired, handleTimeUp } = useTimer();
  
  // State to track challenge status
  const [challengeStatus, setChallengeStatus] = useState(sessionData?.challenge_status || 'cs_not_started');
  
  // Function to save responses to Supabase - memoized with useCallback to capture latest responses
  const saveResponsesToSupabase = useCallback(async () => {
    // Only save if challenge is started, we have an ID, and there are responses to save
    if (challengeStatus !== 'cs_started' || !challengeInstanceId || !Object.keys(responses).length || !data?.questions) {
      return;
    }

    try {
      // Get formatted question answers using the shared function
      const questionAnswers = formatResponses();
      
      console.log("Auto-saving challenge responses to Supabase:", questionAnswers);

      // Update the record in Supabase
      const { data: supabaseData, error } = await supabase
        .from('challenge_instance')
        .update({ challenge_response_json: questionAnswers })
        .eq('ci_id', challengeInstanceId);

      if (error) {
        console.error('Error auto-saving to Supabase:', error);
      } else {
        console.log('Successfully auto-saved challenge responses to Supabase:', new Date().toISOString());
      }
    } catch (err) {
      console.error('Exception auto-saving challenge responses to Supabase:', err);
    }
  }, [challengeStatus, challengeInstanceId, responses, data]);

  // Auto-save responses to Supabase every X seconds
  useEffect(() => {
    // Only set up auto-save if challenge is started
    if (challengeStatus !== 'cs_started') {
      return;
    }
    
    // Set up interval to save responses
    const autoSaveInterval = setInterval(saveResponsesToSupabase, 30000); // Save every X seconds

    // Clean up interval on unmount
    return () => clearInterval(autoSaveInterval);
  }, [challengeStatus, saveResponsesToSupabase]);

  // Function to start the challenge
  const startChallenge = async () => {
    setLoading(true);
    try {
      // Send request to start the challenge
      const response = await fetch(
        `${EVALUATION_ENDPOINTS.START}?ci_id=${challengeInstanceId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to start challenge: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      const startResponse = responseData[0];
      
      setStartDate(startResponse.challenge_start_date);
      setChallengeStatus(startResponse.challenge_status);
      
      // Update session data with the new status
      updateSessionData({ 
        challenge_status: startResponse.challenge_status
      });
    } catch (err) {
      console.error('Error starting challenge:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Skip if no eid or if challenge is already completed
      if (!eid) return;
      if (sessionData?.challenge_status === 'cs_done') {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(
          `${EVALUATION_ENDPOINTS.GET}?eid=${eid}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch challenge data: ${response.status} ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log("Challenge responseData:", responseData);
        
        // Get the challenge response data with optional chaining
        const challengeResponse = responseData?.[0];
        if (!challengeResponse) {
          throw new Error('No challenge data found in response');
        }
        
        // QUESTIONNAIRE SKIPPING: Check if questionnaire is completed (only if questionnaire step is enabled)
        if (challengeResponse.steps?.step_questionnaire && 
            challengeResponse.questionnaire_status !== 'qs_done') {
          navigate(`/questionnaire/${eid}`);
          return;
        }
        
        // Set status first - this is important for UI rendering
        setChallengeStatus(challengeResponse.challenge_status);
        updateSessionData({ challenge_status: challengeResponse.challenge_status });
        
        // Set other basic data
        setChallengeInstanceId(challengeResponse.ci_id);
        setStartDate(challengeResponse.challenge_start_date);
        setTimerDuration(challengeResponse.max_duration_minutes);
        
        // Only try to set challenge data if it exists
        // This handles the abandoned case where challenge data might not be present
        if (challengeResponse.challenge) {
          setData(challengeResponse.challenge);
          
          // If there's autosaved response data, prefill the form with it
          if (challengeResponse.challenge_response_json?.length > 0) {
            console.log("Prefilling form with autosaved data:", challengeResponse.challenge_response_json);
            
            // Convert the array of question answers to an object keyed by question id
            const prefillResponses = {};
            
            // Process each item
            for (const item of challengeResponse.challenge_response_json) {
              if (item && item.id) {
                // For audio-answer questions, preserve file_id and file_key, and get signed URL
                if (item.file_id && item.file_key) {
                  try {
                    const response = await fetch(OTHER_ENDPOINTS.SIGN_URL, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ file_key: item.file_key })
                    });
                    
                    const data = await response.json();
                    
                    prefillResponses[item.id] = {
                      answer: item.answer,
                      file_id: item.file_id,
                      file_key: item.file_key,
                      url: data[0]?.signed_url
                    };
                  } catch (error) {
                    console.error('Error fetching signed URL:', error);
                    prefillResponses[item.id] = {
                      answer: item.answer,
                      file_id: item.file_id,
                      file_key: item.file_key
                    };
                  }
                }
                // For single-answer and multiple-answer questions, preserve the otherAnswer field if it exists
                else if (item.otherAnswer !== undefined) {
                  prefillResponses[item.id] = {
                    answer: item.answer,
                    otherAnswer: item.otherAnswer
                  };
                } 
                // For other question types, just store the answer
                else {
                  prefillResponses[item.id] = item.answer;
                }
              }
            }
            
            // Set the responses state with the prefilled data
            setResponses(prefillResponses);
          }
        }
        
      } catch (err) {
        console.error('Error fetching challenge data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eid, sessionData?.challenge_status, navigate]);

  // Cleanup effect to clear pending timeouts when component unmounts
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const { 
    showAbandonConfirm, 
    handleAbandonClick, 
    handleCancelAbandon, 
    handleConfirmAbandon 
  } = useAbandonConfirmation(challengeInstanceId, eid, EVALUATION_ENDPOINTS.ABANDON, 'challenge_status', 'cs_abandoned');
  
  const handlePrevious = () => {
    navigatePrevious(location.pathname);
  };

  const handleChange = (questionId, value) => {
    // If timer has expired, don't allow changes
    if (timerExpired) return;
    
    // Update responses using functional update pattern
    setResponses(prev => {
      const updated = { ...prev, [questionId]: value };
      console.log('User Answers Updated:', JSON.stringify(updated, null, 2));
      return updated;
    });
  };

  // Render a single question
  const renderQuestionItem = (question, index) => {
    // Get the question type from the question object
    let questionType = question.type;
    
    return (
      <div key={question.id} className="mb-10 pb-8 border-b border-gray-200 transition-all duration-300 hover:shadow-md hover:bg-gray-50 rounded-lg p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <div className="text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 shadow-sm" style={{ backgroundColor: '#33C6B6' }}>
              <span className="font-bold">{index + 1}</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800">
              {getQuestionTypeLabel(question.type)}
            </h3>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              openReportPopup(question.id, question);
            }}
            className="text-sm bg-white hover:bg-gray-50 text-black border border-black px-3 py-1 rounded-full flex items-center transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Report
          </button>
        </div>
        <p className="text-lg mb-2 text-gray-900 font-medium">{question.questionText}</p>
        
        {/* Display question details if available */}
        {question.questionDetails && (
          <p className="text-md mb-0 font-medium text-gray-700">{question.questionDetails}</p>
        )}
        
        {/* Display code snippet if available */}
        {question.codeSnippet && (
          <div className="mt-4 mb-0">
            <CodeSnippetViewer 
              code={question.codeSnippet}
              skillsTested={question.skillsTested}
            />
          </div>
        )}

        {/* Display skills tested if available */}
        {question.skillsTested && question.skillsTested.length > 0 && (
          <div className="mt-4 mb-4">
            <span className="text-sm text-gray-500">Skills: </span>
            {question.skillsTested.map((skill, index) => (
              <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                {skill}
              </span>
            ))}
          </div>
        )}        
        
        {/* Render the appropriate question component based on type */}
        <div className="mt-4 pl-4 border-l-4 border-blue-200 py-1">
          {questionType === 'multiple-answer' && (
            <MultiOptionsQuestion 
              question={question} 
              value={responses[question.id] || []} 
              onChange={(value) => handleChange(question.id, value)}
              disabled={timerExpired}
            />
          )}
        
        {questionType === 'single-answer' && (
          <SingleOptionQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'true-false' && (
          <TrueFalseQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'long-text' && (
          <LongTextQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'short-text' && (
          <ShortTextQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'audio-answer' && (
          <AudioAnswerQuestionWithSave 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
            challengeInstanceId={challengeInstanceId}
          />
        )}
        
        {questionType === 'video-answer' && (
          <VideoAnswerQuestionWithSave 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
            challengeInstanceId={challengeInstanceId}
          />
        )}
        
        {questionType === 'code-completion' && (
          <CodeCompletionQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'code-explanation' && (
          <CodeExplanationQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'code-refactoring' && (
          <CodeRefactoringQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'code-write' && (
          <CodeWriteQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'find-the-bug' && (
          <FindTheBugQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
        {questionType === 'security-task' && (
          <SecurityTaskQuestion 
            question={question} 
            value={responses[question.id] || ''} 
            onChange={(value) => handleChange(question.id, value)}
            disabled={timerExpired}
          />
        )}
        
          {questionType === 'performance-optimization' && (
            <PerformanceOptimizationQuestion 
              question={question} 
              value={responses[question.id] || ''} 
              onChange={(value) => handleChange(question.id, value)}
              disabled={timerExpired}
            />
          )}
        </div>
      </div>
    );
  };

  // Calculate the number of answered questions
  const getAnsweredQuestionsCount = () => {
    if (!data || !data.questions) return 0;
    
    // Count questions that have a non-empty response
    return data.questions.reduce((count, question) => {
      const response = responses[question.id];
      
      // For multiple-answer questions, check if the array has items
      if (question.type === 'multiple-answer') {
        return response && response.answer && response.answer.length > 0 ? count + 1 : count;
      }
      
      // For single-answer questions with otherAnswer field
      if (question.type === 'single-answer') {
        return (response && (response.answer || response.otherAnswer)) ? count + 1 : count;
      }
      
      // For audio-answer questions, check if answer object with file_key exists
      if (question.type === 'audio-answer') {
        return (response && typeof response === 'object' && response.file_key) ? count + 1 : count;
      }
      
      // For video-answer questions, check if answer object with file_key exists
      if (question.type === 'video-answer') {
        return (response && typeof response === 'object' && response.file_key) ? count + 1 : count;
      }
      
      // For other question types, check if response exists and is not empty
      return response && response !== '' ? count + 1 : count;
    }, 0);
  };
  
  // Get the count of answered questions
  const answeredQuestionsCount = getAnsweredQuestionsCount();
  
  // Check if both questionnaire_status and challenge_status are "Done"
  useEffect(() => {
    if (sessionData?.questionnaire_status === 'qs_done' && sessionData?.challenge_status === 'cs_done') {
      navigate(`/finished/${eid}`);
    }
  }, [sessionData, navigate, eid]);

  return (
    <div className="flex flex-col min-h-screen p-0 pt-20 pb-16 w-full">
      {/* Sticky Top Menu with Countdown Timer (only shown when active) */}
      <StickyMenu 
        startDate={challengeStatus === 'cs_started' ? startDate : null}
        timerDuration={challengeStatus === 'cs_started' ? timerDuration : null}
        onTimeUp={handleTimeUp}
      />
      
      {/* Time's Up box - only shown when timer has expired */}
      <TimeExpiredBox 
        isVisible={timerExpired} 
        onSubmit={() => document.getElementById('submit-challenge-button').click()} 
        buttonText="Submit Challenge"
      />
      
      {/* 2-Column Layout Container */}
      <div className="flex-1 flex justify-center px-4">
        <div className={`w-full bg-white rounded-3xl shadow-md flex ${
          challengeStatus === 'cs_started' ? 'max-w-[1100px]' : 'max-w-[800px]'
        }`}>
          {/* Main Content */}
          <div className={`flex-1 py-12 px-8 flex flex-col items-center relative ${
            challengeStatus === 'cs_started' ? 'border-r border-gray-200' : ''
          }`}>
            {/* Status Pill - only show when not started or in other non-active states */}
            {challengeStatus !== 'cs_started' && (
              <div className="absolute top-4 right-4">
                <StatusPill status={challengeStatus} />
              </div>
            )}
            
            {/* Show appropriate header based on challenge status */}
            {challengeStatus === 'cs_not_started' ? (
              /* Simple title for not started state */
              <div className="relative w-full mb-4">
                <h2 className="text-3xl font-semibold text-center">Challenge</h2>
              </div>
            ) : challengeStatus === 'cs_started' ? (
              /* Simple header when challenge is active */
              <h2 className="text-3xl font-semibold mb-8 text-center">Challenge</h2>
            ) : null}
        
        {/* Anti-cheating component */}
        <AntiCheating onCheatingDetected={handleCheatingDetected} />
        
        {/* Report Question Popup */}
        <ReportQuestionPopup 
          isOpen={reportPopupOpen} 
          onClose={closeReportPopup} 
          questionId={currentQuestionId}
          eval_id={eid}
          type="Challenge"
          question={currentQuestion}
        />
        
        {/* If challenge is not started, show confirmation screen */}
        {challengeStatus === 'cs_not_started' && !loading ? (
          <ConfirmationScreen
            title="Ready To Start?"
            description="Once started, please complete the challenge in one session." // Once started, the timer will begin and you must complete the challenge in one session. If the time runs out, the challenge will expire.
            timeRequired={`${timerDuration} minutes`}
            onStart={startChallenge}
            onBack={handlePrevious}
            startButtonText="Start Challenge"
            backButtonText="Back"
            icon="question"
            faqType="challenge"
          />
        ) : challengeStatus === 'cs_abandoned' ? (
          // NOTE: This AbandonedScreen is currently unreachable because WelcomeScreen.js 
          // handles all abandoned states globally. To reach this code, you would need to:
          // 1. Comment out the abandoned check in WelcomeScreen.js
          // 2. Remove the abandoned redirect restriction in ProtectedRoute.js
          <AbandonedScreen
            title="Challenge Expired"
            message="This challenge has expired or was abandoned."
            onBack={() => navigate(`/${eid}`)}
            backButtonText="Start Page"
          />
        ) : challengeStatus === 'cs_done' ? (
          <CompletedScreen
            title="Challenge Completed"
            message="You have already completed this challenge."
            onContinue={() => navigate(`/finished/${eid}`)}
            continueButtonText="View Results"
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 mb-4" style={{ borderTopColor: '#33C6B6', borderBottomColor: '#33C6B6' }}></div>
            <div className="text-xl font-medium" style={{ color: '#33C6B6' }}>Loading challenge questions...</div>
          </div>
        ) : error ? (
          <div className="text-lg text-red-500">Error: {error}</div>
        ) : (
          <div className="w-full">
          {data && data.questions && (
            <form>
              {/* {data.quizTitle && (
                <h3 className="text-xl font-medium mb-4">{data.quizTitle}</h3>
              )}
              
              {data.description && (
                <p className="mb-6 text-gray-600">{data.description}</p>
              )}
              
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-2">Total questions: {data.questions.length}</p>
              </div> */}
              
              
              <div className="space-y-4 mb-8">
                {data.questions.map((question, index) => renderQuestionItem(question, index))}
              </div>
              
              <div className="flex justify-center w-full mt-10">
                <button
                  id="submit-challenge-button"
                  type="button"
                  onClick={() => setShowSubmitConfirm(true)}
                  disabled={isSubmitting}
                  className={`text-white rounded-lg px-8 py-4 text-xl shadow-md hover:shadow-lg flex items-center ${
                    isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transition-colors duration-200'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="inline-block animate-spin mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Challenge
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
          </div>
        )}
          </div>
          
          {/* Right Column - Sidebar - Only show when challenge is started */}
          {challengeStatus === 'cs_started' && (
            <div 
              className="w-80 py-10 px-8 rounded-r-3xl flex flex-col justify-between relative" 
              style={{ 
                backgroundColor: '#F9F9F9',
                backgroundImage: 'url(/leftbg.svg), url(/hotcircle.svg)',
                backgroundSize: '120% auto, auto',
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'top center, bottom center'
              }}
            >
              <div className="space-y-8">
                {/* Description section */}
                <div>
                  <h2 className="text-2xl font-regular text-gray-900 mb-4 leading-tight">
                    Validate your skills and knowledge.
                  </h2>
                  <p className="text-gray-600 text-sm">
                    This challenge is assessed and scored.
                  </p>
                </div>

                {/* Status section */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Challenge Status
                  </h3>
                  <StatusPill status={challengeStatus} />
                </div>

                {/* Elapsed Time section */}
                <ElapsedTime startDate={startDate} />

                {/* Progress section */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Completion Progress
                  </h3>
                  <div className="flex justify-center">
                    <CompletionCircle 
                      completedQuestions={answeredQuestionsCount} 
                      totalQuestions={data?.questions?.length || 0} 
                    />
                  </div>
                </div>
              </div>

              {/* Bottom section with links and footer */}
              <div className="space-y-6">
                {/* Cheating monitoring warning */}
                <div className="rounded-lg p-3 mb-12 mt-8 relative" style={{ backgroundColor: '#FEF3C7' }}>
                  <div className="flex items-center">
                    <img 
                      src="/nocheatingicon.svg" 
                      alt="Fingers crossed" 
                      className="w-8 h-8 mr-3 flex-shrink-0"
                      style={{ filter: 'brightness(0) saturate(100%) invert(8%) sepia(50%) saturate(7500%) hue-rotate(0deg) brightness(95%) contrast(107%)' }}
                    />
                    <p className="text-sm text-left" style={{ color: '#7C0000' }}>
                      Integrity and authenticity checks are enabled.
                    </p>
                  </div>
                </div>
                {/* Action links */}
                <div className="flex justify-between items-center px-2">
                  <button
                    onClick={handleAbandonClick}
                    className="flex items-center text-red-600 hover:text-red-700 text-sm transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Abandon
                  </button>
                  
                  <button className="flex items-center text-gray-600 hover:text-gray-700 text-sm transition-colors duration-200">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Help
                  </button>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-300 pt-4">
                  <div className="text-xs text-gray-500 text-center">
                    Powered by tendent.ai
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Abandon Confirmation Modal */}
      <AbandonConfirmationModal
        isOpen={showAbandonConfirm}
        onCancel={handleCancelAbandon}
        onConfirm={handleConfirmAbandon}
        title="Abandon Challenge?"
        message="Are you sure you want to abandon this challenge? This action cannot be undone and your progress will be lost."
      />
      
      {/* Submit Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSubmitConfirm}
        onCancel={() => setShowSubmitConfirm(false)}
        onConfirm={submitChallenge}
        title="Submit Challenge?"
        message="Are you sure you want to submit your challenge? Please review your answers before submitting."
      />
    </div>
  );
}

export default Challenge;
